<template>
  <div class="flex flex-col w-full flex-1 gap-[50px] h-full">
    <DashboardCards
      :order_items_by_status_group="
        dashboardData?.order_items_by_status_group ?? []
      "
      :totalOrders="dashboardData?.items.total ?? 0"
      v-if="dashboardData && !isSuperOrOrderAdmin() && permissionLoaded"
    />

    <!-- <DashboardTabs
      :tabs="filteredModeTabs"
      :currentTab="currentMode"
      @tab-change="onModeTabChange"
      type="mode"
    /> -->

    <div class="flex flex-col w-full flex-1 gap-[20px] overflow-y-auto">
      <!-- Dashboard Mode with Splitter -->
      <template v-if="currentMode === 'dashboard'">
        <template v-if="selectedRow">
          <DashboardSplitter
            direction="horizontal"
            :initialSplit="splitterSize"
            :minSize="50"
            :maxSize="50"
            localStorageKey="dashboard-splitter-size"
            @resize="handleResize"
            :showSecondPanel="!!selectedOrderDetails"
            v-if="selectedOrderDetails"
          >
            <template #first>
              <div
                class="flex flex-col w-full flex-1 gap-[20px] pt-[40px] px-[30px] pb-[40px]"
              >
                <DashboardTabs
                  :tabs="filteredModeTabs"
                  :currentTab="currentMode"
                  @tab-change="onModeTabChange"
                  type="mode"
                  v-if="can('can-see-calendar')"
                />
                <div class="flex justify-between gap-4 w-full flex-wrap">
                  <DashboardTabs
                    :tabs="dashboardTabs"
                    :currentTab="current_filters?.items_for ?? null"
                    type="dashboard"
                    @tab-change="(tab) => onTabChange(tab)"
                  />
                  <div class="flex justify-end gap-2 flex-1 flex-wrap">
                    <DashboardFilters
                      @update-filter="
                        (filter: string | null) => getQuickFilterData(filter)
                      "
                      :currentTab="currentTab"
                      :current-mode="'dashboard'"
                    />
                    <AssignOrderAdminModal
                      @onSave="fetchOnSave(true)"
                      v-if="can('assign-order-administrator')"
                    />
                    <ExportPriceModal v-if="can('export-order-prices')" />
                    <button
                      v-if="can('manual-create-order')"
                      class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[#00B087] border-[1px] border-[#00B087] bg-white rounded-[10px] whitespace-nowrap"
                      @click="createManualOrder = true"
                    >
                      <PlusCircleIcon class="size-5 text-[#00B087]" />
                      Create Order
                    </button>
                  </div>
                </div>

                <DataTable
                  :key="currentTab"
                  :data="tableData"
                  :columns="columns"
                  :show-actions-column="true"
                  :pageSizeOptions="[10, 20, 50, 100]"
                  :isAsync="true"
                  :totalItems="totalItems"
                  @fetch-data="(data: any) => fetchRecords(data)"
                  :currentPaginationInfo="current_filters"
                  :searchable="true"
                  search-placeholder="Type to search"
                  :isLoading="isPending"
                  :isFetching="isFetching"
                  :error="error"
                  @cell-click="handleCellClick"
                  :showPagination="true"
                >
                  <template #actions="{ row }">
                    <DashboardActions
                      :current-tab="currentTab"
                      @onFetch="fetchOnSave"
                      :order_item="row"
                    />
                  </template>
                  <template #loading>
                    <div class="flex justify-center items-center p-8">
                      <div
                        class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"
                      ></div>
                      <span class="ml-2 text-gray-600">Loading orders...</span>
                    </div>
                  </template>
                  <template #error>
                    <div
                      class="flex justify-center items-center p-8 text-red-600"
                    >
                      <span>Error loading orders. Please try again.</span>
                    </div>
                  </template>
                </DataTable>
              </div>
            </template>

            <template #second>
              <div
                class="flex flex-col h-full px-[50px] py-[40px] relative overflow-y-auto"
              >
                <!-- Loading overlay for sidepane -->
                <div
                  v-if="isSidepaneLoading"
                  class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg"
                >
                  <div class="flex flex-col items-center gap-3">
                    <div
                      class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
                    ></div>
                    <span class="text-gray-600 font-medium"
                      >Updating order...</span
                    >
                  </div>
                </div>

                <span
                  @click="
                    () => {
                      selectedOrderDetails = null;
                      selectedRow = null;
                    }
                  "
                  class="flex justify-end absolute top-5 right-5 cursor-pointer z-50 bg-gray-400 rounded-full p-2 text-white hover:bg-gray-600"
                >
                  <XMarkIcon class="text-white size-5" />
                </span>

                <Tabs
                  :tabs="tabs"
                  initial-tab="timeline"
                  :key="selectedOrderDetails.id"
                >
                  <template #timeline>
                    <div class="p-5">
                      <OrderTimeline
                        :order="selectedOrderDetails"
                        :order_item="currentOrderItem"
                        :order_item_index="currentOrderItemIndex"
                        :order_no="selectedOrderDetails.order_no"
                        :isLoading="isSidepaneLoading"
                        @orderMoved="handleOrderMoved"
                        @orderUpdated="handleOrderUpdated"
                      />
                    </div>
                  </template>
                  <template #detail>
                    <FactoryProductsTab
                      v-if="currentOrderItem.factory_products?.length"
                      :factoryProducts="currentOrderItem.factory_products"
                      :order="selectedOrderDetails"
                      :order_item_price="
                        selectedRow.price_info?.order_item_final_price
                      "
                      @openOrderPriceModal="() => (priceBreakdownModal = true)"
                    />
                    <p class="text-gray-700" v-else>
                      No factory products available.
                    </p>
                  </template>
                  <template #files v-if="currentOrderItem?.factory_id">
                    <div class="p-5 border border-gray-200 rounded-lg">
                      <h2 class="text-xl font-semibold mb-3">Files Content</h2>
                      <OrderFiles
                        :order="currentOrderItem"
                        :order-id="currentOrderItem.order_id"
                        :order-factories="[
                          {
                            id: currentOrderItem.factory_id,
                            name: currentOrderItem.factory_name,
                          },
                        ]"
                      />
                    </div>
                  </template>
                </Tabs>
              </div>
            </template>
          </DashboardSplitter>
        </template>

        <!-- Dashb`oard mode without splitter -->
        <template v-else>
          <div
            class="flex flex-col w-full flex-1 gap-[20px] px-[30px] py-[40px] h-full overflow-y-auto"
          >
            <DashboardTabs
              :tabs="filteredModeTabs"
              :currentTab="currentMode"
              @tab-change="onModeTabChange"
              type="mode"
              v-if="can('can-see-calendar')"
            />
            <div class="flex justify-between gap-4 w-full flex-wrap">
              <DashboardTabs
                :tabs="dashboardTabs"
                :currentTab="current_filters?.items_for ?? null"
                @tab-change="(tab) => onTabChange(tab)"
                type="dashboard"
              />
              <div class="flex justify-end gap-2 flex-1 flex-wrap">
                <DashboardFilters
                  @update-filter="
                    (filter: string | null) => getQuickFilterData(filter)
                  "
                  :currentTab="currentTab"
                  :current-mode="'dashboard'"
                />
                <AssignOrderAdminModal
                  @onSave="fetchOnSave(true)"
                  v-if="can('assign-order-administrator')"
                />
                <ExportPriceModal v-if="can('export-order-prices')" />
                <button
                  v-if="can('manual-create-order')"
                  class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[#00B087] border-[1px] border-[#00B087] bg-white rounded-[10px] whitespace-nowrap"
                  @click="createManualOrder = true"
                >
                  <PlusCircleIcon class="size-5 text-[#00B087]" />
                  Create Order
                </button>
              </div>
            </div>

            <DataTable
              :key="currentTab"
              :data="tableData"
              :columns="columns"
              :show-actions-column="true"
              :pageSizeOptions="[10, 20, 50, 100]"
              :isAsync="true"
              :totalItems="totalItems"
              @fetch-data="(data: any) => fetchRecords(data)"
              :currentPaginationInfo="current_filters"
              :searchable="true"
              search-placeholder="Type to search"
              :isLoading="isPending"
              :isFetching="isFetching"
              :error="error"
              @cell-click="handleCellClick"
              :showPagination="true"
            >
              <template #actions="{ row }">
                <DashboardActions
                  :current-tab="currentTab"
                  @onFetch="fetchOnSave"
                  :order_item="row"
                />
              </template>
              <template #loading>
                <div class="flex justify-center items-center p-8">
                  <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"
                  ></div>
                  <span class="ml-2 text-gray-600">Loading orders...</span>
                </div>
              </template>
              <template #error>
                <div class="flex justify-center items-center p-8 text-red-600">
                  <span>Error loading orders. Please try again.</span>
                </div>
              </template>
            </DataTable>
          </div>
        </template>
      </template>

      <!-- Calendar Mode -->
      <template v-else-if="currentMode === 'calendar'">
        <div
          class="shadow-lg rounded-[10px] overflow-hidden px-[30px] py-[40px] flex flex-col h-full gap-[20px] overflow-y-auto"
        >
          <DashboardTabs
            :tabs="filteredModeTabs"
            :currentTab="currentMode"
            @tab-change="onModeTabChange"
            type="mode"
          />
          <div class="flex justify-between gap-2 flex-1 flex-wrap my-2 mx-2">
            <div class="flex items-center gap-2">
              <Select
                :options="orderOptions"
                v-model="dashboardStore.selectedOrder"
                label="order_no"
                track-by="id"
                :filterable="true"
                :clearable="true"
                @search="onOrderSearch"
                @clear="dashboardStore.jumpToOrder()"
                placeholder="Search by Order No."
                class="min-w-[300px]"
              />
              <button
                class="px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center gap-1"
                @click="dashboardStore.jumpToOrder"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 16l4-4-4-4m8 8V8"
                  />
                </svg>
                Jump
              </button>
            </div>

            <div class="flex gap-2">
              <DashboardFilters
                @update-filter="
                  (filter: string | null) => onCalendarFilterChange(filter)
                "
                :currentTab="currentTab"
                :currentMode="currentMode"
              />
              <button
                class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[#00B087] border-[1px] border-[#00B087] bg-white rounded-[10px] whitespace-nowrap"
                @click="dashboardStore.exportCalendarData"
              >
                <ArrowUpTrayIcon class="size-5 text-[#00B087]" />
                Export Data
              </button>
            </div>
          </div>

          <Calender
            :key="dashboardStore.calendarKey"
            :initial-events="dashboardStore.events"
            :initial-holidays="dashboardStore.holidays as any"
            :allow-add-events="true"
            :allow-edit-events="true"
            :allow-delete-events="true"
            :show-sidebar="true"
            :show-mini-calendar="false"
            :show-upcoming-events="true"
            :initial-view="
              dashboardStore.currentView as 'day' | 'week' | 'month' | 'year'
            "
            :max-events-per-day="5"
            @event-updated="dashboardStore.handleEventUpdated"
            @event-created="dashboardStore.handleEventCreated"
            @event-deleted="dashboardStore.handleEventDeleted"
            @holiday-added="dashboardStore.handleHolidayAdded"
            @holiday-updated="dashboardStore.handleHolidayUpdated"
            @holiday-removed="dashboardStore.handleHolidayRemoved"
            @refresh-requested="dashboardStore.fetchCalendarData"
            @view-changed="handleViewChanged"
            @date-selected="handleDateSelected"
            :order-no-search="dashboardStore.orderNoSearch"
            :event-type-filter="dashboardStore.eventTypeFilter"
            :event-status-filter="dashboardStore.eventStatusFilter"
            :selected-date="dashboardStore.selectedCalendarDate"
            @jump-to-order="dashboardStore.jumpToOrder"
          >
            <!-- Custom event tile template -->
            <template #event-tile="{ event, view, deletable, deleteEvent }">
              <div class="relative">
                <!-- Delete button -->
                <button
                  v-if="deletable"
                  class="absolute top-0 right-0 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  @click.stop="deleteEvent(event.id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3 w-3 text-red-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>

                <!-- Custom event content -->
                <div class="flex items-center space-x-1">
                  <!-- Event type icon -->
                  <div class="flex-shrink-0">
                    <svg
                      v-if="event.type === 'order'"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                      <path
                        fill-rule="evenodd"
                        d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <svg
                      v-else-if="event.type === 'meeting'"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <svg
                      v-else-if="event.type === 'reminder'"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                      />
                    </svg>
                  </div>

                  <!-- Event title and details -->
                  <div class="flex-1 min-w-0">
                    <p class="font-medium truncate">{{ event.title }}</p>
                    <div class="flex text-xs text-gray-500 space-x-1">
                      <span v-if="event.order_no"
                        >Order# {{ event.order_no }}</span
                      >
                      <span v-if="event.status" class="capitalize">
                        <span
                          class="inline-block w-2 h-2 rounded-full mr-1"
                          :class="getStatusColor(event.status)"
                        ></span>
                        {{ event.status }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Calender>
        </div>
      </template>
    </div>
  </div>

  <Transition name="fade">
    <CreateManualOrderModal
      v-if="createManualOrder"
      @onSave="fetchOnSave(true)"
      @close="() => (createManualOrder = false)"
    />
  </Transition>
  <Transition name="fade">
    <FactoryAssignModal
      v-if="assignFactoryModal"
      :orderItem="selectedOrderItem"
      @onSave="fetchOnSave(true)"
      @close="factoryModalClose"
    />
  </Transition>
  <Transition name="fade">
    <PriceBreakdownModal
      :order_item="selectedRow"
      :appUrl="appURL"
      :can="can"
      v-if="priceBreakdownModal"
      @close="handlePriceBreakDownModalCloseEvent"
    />
  </Transition>
</template>

<script setup lang="ts">
import DashboardActions from "@/components/Dashboard/DashboardActions.vue";
import DashboardCards from "@/components/Dashboard/DashboardCards.vue";
import DashboardFilters from "@/components/Dashboard/DashboardFilters.vue";
import DashboardSplitter from "@/components/Dashboard/DashboardSplitter.vue";
import DashboardTabs from "@/components/Dashboard/DashboardTabs.vue";
import AssignOrderAdminModal from "@/components/Dashboard/Modals/AssignOrderAdminModal.vue";
import CreateManualOrderModal from "@/components/Dashboard/Modals/CreateManualOrderModal.vue";
import ExportPriceModal from "@/components/Dashboard/Modals/ExportPriceModal.vue";
import FactoryAssignModal from "@/components/Dashboard/Modals/FactoryAssignModal.vue";
import FactoryProductsTab from "@/components/Dashboard/Order/components/FactoryProductsTab.vue";
import Calender from "@/components/Shared/Calender.vue";
import DataTable from "@/components/Shared/DataTable.vue";
import Select from "@/components/Shared/Select.vue";
import Tabs from "@/components/Shared/Tabs.vue";
import Tooltip from "@/components/Shared/Tooltip.vue";

import OrderTimeline from "@/components/Dashboard/Order/OrderTimeline.vue";
import UnderlineTabs from "@/components/Shared/UnderlineTabs.vue";
import {
  can,
  CustimooOrderFlowStatuses,
  formatDate,
  isSuperOrOrderAdmin,
  logError,
  timeAgo,
} from "@/composables";
import { useVueQuery } from "@/composables/useVueQuery";
import { http } from "@/interceptors/axiosRequestInterceptor";
import {
  DashboardItem,
  DashboardRequest,
  Order,
  OrderAdminUser,
  OrderDetail,
  OrderDetailItem,
  Urgency,
} from "@/models";
import { useMainStore } from "@/store";
import { useDashboardStore } from "@/store/dashboard";

import {
  ArrowUpTrayIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";

import { ChevronDownIcon, KeyIcon } from "@heroicons/vue/24/solid";
//@ts-ignore
import OrderFiles from "@/components/Dashboard/Order/OrderFiles.vue";
import { useQueryClient } from "@tanstack/vue-query";
import { onClickOutside } from "@vueuse/core";
import { storeToRefs } from "pinia";

import PriceBreakdownModal from "@/components/Dashboard/Modals/PriceBreakdownModal.vue";
import {
  computed,
  ComputedRef,
  h,
  onMounted,
  reactive,
  ref,
  VNode,
  watch,
} from "vue";

// Store instances
const store = useMainStore();
const dashboardStore = useDashboardStore();
const appURL = import.meta.env.VITE_VUE2_APP_URL;

// Store refs
const {
  dashboardData,
  dashboardTabs,
  permissionLoaded,
  orderAdmins,
  dashboardQuickFilters,
  loading,
  sidebarOpen,
} = storeToRefs(store);

const {
  modeTabs,
  currentMode,
  events,
  holidays,
  selectedCalendarDate,
  orderNoSearch,
  eventTypeFilter,
  eventStatusFilter,
  currentView,
  selectedHolidayCountryCode,
  calendarFilters,
  orderList,
  selectedOrder,
  orderInfo,
  isOrderLoading,
  isCalendarLoading,
  calendarKey,
} = storeToRefs(dashboardStore);

// Local state
interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  renderer?: (value: any, row: any) => string | VNode | number;
}

interface OrderPage {
  items: DashboardItem[];
  total: number;
  nextPage?: number;
}

const queryClient = useQueryClient();

const currentTab = ref("all_items");
const selectedFilter = ref<string | null>(null);
const createManualOrder = ref<boolean>(false);
const assignFactoryModal = ref<boolean>(false);
const selectedOrderDetails = ref<OrderDetail>(null);
const isSidepaneLoading = ref<boolean>(false);
const priceBreakdownModal = ref<boolean>(false);
const splitterSize = ref(50); // Default split size
const currentSplitterSize = ref(50);
const selectedOrderItem = ref<DashboardItem>(null);
const selectedUrgentOrderFilter = ref<string | null>(null);

// Dashboard request params
let defParams: DashboardRequest = {
  items_for: currentTab.value ?? "all_items",
  urgent_orders: null,
  sort_by: "order_no",
  sort_order: "desc",
  page: 1,
  search: "",
  per_page: 10,
};

const defParamsString = JSON.stringify({ dashboardRequest: defParams });
const currentDashboardModel: Record<string, DashboardRequest> = JSON.parse(
  localStorage.getItem("dashboardRequest") ?? defParamsString,
);
const current_filters = ref<DashboardRequest>(
  currentDashboardModel.dashboardRequest,
);
const { data, error, isFetching, isPending } = useVueQuery<OrderPage, Error>(
  "orders",
  async () => {
    const params: DashboardRequest = {
      ...defParams,
      ...current_filters.value,
      page: current_filters.value.page,
    };
    await store.getDashboardData(params, dashboardQuickFilters.value);
    const currentData = dashboardData.value?.items;
    return {
      items: currentData?.data ?? [],
      total: currentData?.total ?? 0,
    };
  },
  {
    queryKey: [
      "orders",
      currentTab,
      selectedFilter,
      selectedUrgentOrderFilter,
      dashboardQuickFilters,
      defParams.sort_by,
      defParams.sort_order,
      defParams.search,
      current_filters.value.page,
    ],
    refetchOnWindowFocus: false,
  },
);

const tabs = [
  { id: "timeline", label: "Timeline" },
  { id: "detail", label: "Detail" },
  { id: "files", label: "Files" },
];

// Computed properties
const canAssignOrderAdmin = computed(() => can("assign-order-administrator"));
const selectedRow = ref<DashboardItem | null>(null);

const canViewShippingAddress = computed(() =>
  can("order-listing-shipping-address-column"),
);
const canViewMerchant = computed(() => can("order-listing-merchant-column"));
const canViewFactory = computed(() => can("order-listing-factory-column"));

// Filter mode tabs based on permissions
const filteredModeTabs = computed(() => {
  return modeTabs.value.filter((tab) => {
    if (tab.id === "calendar") {
      return can("can-see-calendar");
    }
    return true;
  });
});

// Helper function for custom event tile
const getStatusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "bg-yellow-500";
    case "processing":
      return "bg-blue-500";
    case "completed":
      return "bg-green-500";
    case "cancelled":
      return "bg-red-500";
    case "delayed":
      return "bg-orange-500";
    default:
      return "bg-gray-500";
  }
};
const orderOptions = computed(() =>
  orderList.value.map((order) => ({
    ...order,
    label: order.customer_reference_no
      ? `${order.order_no} (${order.customer_reference_no})`
      : order.order_no,
  })),
);
const handlePriceBreakDownModalCloseEvent = (fetch_order_items = false) => {
  priceBreakdownModal.value = false;
  if (fetch_order_items) {
    fetchOnSave(true);
  }
};
const onOrderSearch = async (query: string, loading: (b: boolean) => void) => {
  loading(true);
  await dashboardStore.searchOrders(query);
  loading(false);
};

// Event handlers
const onModeTabChange = (tabId: string) => {
  dashboardStore.setCurrentMode(tabId);

  // Only initialize calendar when switching to calendar mode for the first time
  if (tabId === "calendar" && !dashboardStore.calendarInitialized) {
    dashboardStore.initializeCalendar();
  }
};

const onOrderNoSearch = (val: string) => {
  dashboardStore.setOrderNoSearch(val);
};

const onEventTypeFilter = (val: string) => {
  dashboardStore.setEventTypeFilter(val);
};

const onEventStatusFilter = (val: string) => {
  dashboardStore.setEventStatusFilter(val);
};

const handleViewChanged = (payload: {
  view: string;
  selected_date: string;
  selected_holiday_country_code: string;
}) => {
  dashboardStore.setCurrentView(payload.view);
  dashboardStore.setSelectedDate(new Date(payload.selected_date));
  dashboardStore.setSelectedHolidayCountryCode(
    payload.selected_holiday_country_code,
  );
};

const handleDateSelected = (date: Date) => {
  console.log("Date selected:", date);
};

const onCalendarFilterChange = (filters: any) => {
  if (currentMode.value === "calendar") {
    if (
      filters &&
      typeof filters === "object" &&
      ("items_for" in filters || "quick_filters" in filters)
    ) {
      // If quick_filters is empty or missing, set to null
      if (
        !filters.quick_filters ||
        Object.keys(filters.quick_filters).length === 0
      ) {
        dashboardStore.setCalendarFilters(null);
      } else {
        dashboardStore.setCalendarFilters(filters);
      }
      // Set the query param for my_assigned_orders
      if (filters.items_for === "my_assigned_orders") {
        dashboardStore.setCalendarFilters({
          ...filters,
          my_assigned_orders: true,
        });
      }
    } else {
      dashboardStore.setCalendarFilters(null);
    }
  } else {
    selectedFilter.value = typeof filters === "string" ? filters : null;
  }
};

const factoryModalClose = () => {
  assignFactoryModal.value = false;
  selectedOrderItem.value = null;
};

// Compute data for the table
const tableData = computed(() => {
  return data.value?.items ?? [];
});

// Compute total items for pagination
const totalItems = computed(() => {
  return data.value?.total ?? 0;
});

onMounted(async () => {
  await store.getFactories();
  await store.getOrderAdmins();
  await store.getUnassignedOrders();
});

const fetchRecords = async (data: any): Promise<void> => {
  // Update the filters
  defParams = {
    items_for: selectedFilter.value ?? currentTab.value,
    urgent_orders: selectedUrgentOrderFilter.value ?? null,
    sort_by: data?.sortColumn ?? "order_no",
    sort_order: data?.sortOrder ?? data?.sort_order,
    page: data?.page ?? 1,
    per_page: data?.pageSize ?? data?.per_page,
    search: data?.searchQuery ?? "",
  };

  // Update current filters
  current_filters.value = {
    ...current_filters.value,
    ...defParams,
  };

  // Save to localStorage
  localStorage.setItem(
    "dashboardRequest",
    JSON.stringify({ dashboardRequest: current_filters.value }),
  );

  // Invalidate the query to trigger a refetch with new parameters
  await queryClient.invalidateQueries({ queryKey: ["orders"] });
};

const onTabChange = async (tab: string): Promise<void> => {
  if (tab === "calendar") {
    currentTab.value = tab;
  } else {
    currentTab.value = tab;
    defParams = {
      ...defParams,
      items_for: currentTab.value,
      urgent_orders: selectedUrgentOrderFilter.value ?? null,
      sort_by: "order_no",
      sort_order: "desc",
      page: 1,
    };
    let localFilterInfo = localStorage.getItem("dashboardRequest");
    current_filters.value = {
      ...JSON.parse(localFilterInfo ?? defParamsString).dashboardRequest,
      ...defParams,
    };
  }
};

const fetchOnSave = async (fetch: boolean) => {
  if (fetch) {
    // Invalidate the query to trigger a refetch
    await queryClient.invalidateQueries({ queryKey: ["orders"] });
    let localFilterInfo: Record<string, DashboardRequest> = JSON.parse(
      localStorage.getItem("dashboardRequest") ?? defParamsString,
    );
    current_filters.value = localFilterInfo.dashboardRequest;
  }
};

const loginCustomer = async (customer_id: number): Promise<void> => {
  loading.value = true;
  await http
    .get(`/customer/information/${customer_id}`)
    .then((res) => {
      let token = res.data.access_token;
      if (res.data.user.company_url) {
        const url = res.data.user.company_url + "/#/";
        window.open(`${url}admin/login?token=${token}`);
      }
    })
    .catch((err) => {
      logError(err);
    });
  loading.value = false;
};

const getUrgencyClass = (urgency: Urgency, id: number): string => {
  switch (urgency) {
    case "VIP":
      return "bg-green-300 !text-[#686666] !p-0 ";
    case "Urgent":
      return "order_cancel !text-[#686666] !p-0";
    case "Extra Attention":
      return "bg-blue-300 !text-[#686666] !p-0";
    default:
      return `!p-0 ${id && selectedRow.value && selectedRow.value.id === id ? "bg-gray-100" : ""}`;
  }
};

const visibleColumns = computed(() => {
  const baseColumns = [
    {
      key: "date",
      label: "Date",
      sortable: true,
      sortKey: "status_updated_at",
      cellClass: (row: DashboardItem) => {
        return selectedRow.value && selectedRow.value.id === row.id
          ? "bg-gray-100"
          : "";
      },
      renderer: (val: any, row: DashboardItem) =>
        formatDate(row.order.created_at ?? Date.now().toString()),
    },
    {
      key:
        currentTab.value === "quote_requested_items" ? "order_no" : "quote_no",
      label:
        currentTab.value === "quote_requested_items" ? `Quote No` : `Order No`,
      sortable: true,
      sortKey: "order_no",
      cellClass: (row: DashboardItem) => {
        return currentTab.value !== "quote_requested_items"
          ? getUrgencyClass((row as DashboardItem).order?.urgency, row?.id)
          : "";
      },
      renderer: (val: any, row: DashboardItem) => {
        const rawSymbol = row.order?.order_type_symbol;
        const urgency = row.order?.urgency;
        const orderNumber = row.order ? `#${row.order.order_no}` : "";
        const cleanedSymbol = rawSymbol?.replace(/i/g, "");
        const allowedSymbols = ["S", "R"];
        const showSymbol = rawSymbol && allowedSymbols.includes(cleanedSymbol);
        const cellContent = h(
          "div",
          {
            class: "w-full h-full flex flex-col", // ensures it fills the td
            style: {
              width: "100%",
              height: "100%",
              cursor: "pointer",
              padding: "2.8rem 2rem",
            },
          },
          [
            h(
              "span",
              {
                height: "100%",
              },
              [
                orderNumber,
                showSymbol &&
                  h(
                    "span",
                    {
                      style: {
                        color: "red",
                        fontWeight: "bold",
                        marginLeft: "4px",
                        fontSize: "1rem",
                      },
                    },
                    cleanedSymbol,
                  ),
              ],
            ),
          ],
        );
        if (urgency) {
          return h(
            Tooltip,
            {
              placement: "right",
              content: `${urgency ? urgency.replace(/_/g, " ") : "normal"} Order`,
              widthClass: "w-full",
            },
            {
              trigger: () => cellContent,
            },
          );
        } else {
          return cellContent;
        }
      },
    },
    {
      key: "",
      label: "Customer",
      sortable: false,
      cellClass: (row: DashboardItem) => {
        return selectedRow.value && selectedRow.value.id === row.id
          ? "bg-gray-100"
          : "";
      },
      renderer: (val: any, row: DashboardItem) => {
        const { customer_name, customer_email } = row.order;
        let returnVal = customer_name?.trim()?.length
          ? customer_name
          : customer_email;
        return h("span", { class: "flex gap-1 items-center justify-between" }, [
          h("span", { class: "whitespace-nowrap" }, returnVal),
          h(
            Tooltip,
            {
              placement: "right",
              content: "Login as Customer",
            },
            {
              trigger: () =>
                h(
                  "button",
                  {
                    class:
                      "bg-[#00B087] text-white text-sm font-medium px-2 p-1 rounded-[4px] cursor-pointer flex gap-1 items-center",
                    onClick: () => {
                      loginCustomer(row.order.customer_id);
                    },
                  },
                  [
                    h(KeyIcon, {
                      class: "w-4 h-4 text-white",
                    }),
                  ],
                ),
            },
          ),
        ]);
      },
    },
    canViewFactory.value && {
      key: "factory_name",
      label: "Factory",
      sortable: false,
      cellClass: (row: DashboardItem) => {
        return selectedRow.value && selectedRow.value.id === row.id
          ? "bg-gray-100"
          : "";
      },
      renderer: (val: any, row: DashboardItem) => row.factory_name ?? "N/A",
    },
    {
      key: "status",
      label: "Status",
      sortable: false,
      cellClass: (row: DashboardItem) => {
        return selectedRow.value && selectedRow.value.id === row.id
          ? "bg-gray-100"
          : "";
      },
      renderer: (val: any, row: DashboardItem) => {
        return h(
          "button",
          {
            tabindex: "-1",
            class: `${row.status} p-2 w-max mx-auto rounded-[4px] flex justify-between items-center`,
          },
          [
            CustimooOrderFlowStatuses[row.status],
            row.status === "order_approve" &&
              h(
                "button",
                {
                  class: "px-1",
                  onClick: () => {
                    const { factory_products, ...rest } = row;
                    assignFactoryModal.value = true;
                    selectedOrderItem.value = {
                      ...rest,
                      factory_products: factory_products.map((prod) => {
                        return {
                          ...prod,
                          selected_factory: prod.selected_factory ?? null,
                        };
                      }),
                    };
                    console.log(
                      "selectedOrderDetails.value",
                      selectedOrderItem.value,
                    );
                  },
                },
                [
                  h(ChevronDownIcon, {
                    class: "text-[#9197B3] size-4",
                  }),
                ],
              ),
          ].filter(Boolean),
        );
      },
    },
  ];

  // Add additional columns when splitter is closed or small
  if (!selectedOrderDetails.value || currentSplitterSize.value < 40) {
    return [
      ...baseColumns,
      canViewShippingAddress.value && {
        key: "",
        label: "Shipping Address",
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id
            ? "bg-gray-100"
            : "";
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) => {
          if (!row.order.shipping_address) return "";
          return `${row.order.shipping_address.address1 || ""} ${row.order.shipping_address.address2 || ""} ${row.order.shipping_address.city || ""}
                  ${row.order.shipping_address.zip_code || ""} ${row.order.shipping_address.country || ""}`;
        },
      },
      {
        key: "",
        label: "Est. Shipping Date",
        sortable: false,
        renderer: (val: any, row: DashboardItem) => {
          if (!row.est_shipping_date) return "N/A";
          return formatDate(
            Array.isArray(row.est_shipping_date)
              ? row.est_shipping_date[row.est_shipping_date.length - 1]
              : row.est_shipping_date,
          );
        },
      },
      canViewMerchant.value && {
        key: "",
        label: "Merchant",
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id
            ? "bg-gray-100"
            : "";
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) => row.order.company_name,
      },
      canAssignOrderAdmin.value && {
        key: "admin",
        label: "Administrator",
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id
            ? "bg-gray-100"
            : "";
        },
        renderer: (val: any, row: DashboardItem) => {
          if (!row.factory_id && row.order.order_administrator?.name)
            return row.order.order_administrator.name;
          if (!row.factory_id) return "N/A";
          const filteredOrderAdmins = orderAdmins.value.filter(
            (admin: OrderAdminUser) =>
              admin.factory_ids?.includes(row.factory_id),
          );
          return h(
            "div",
            {
              onClick: (e: Event) => e.stopPropagation(),
              class: "w-full h-full",
            },
            [
              h(Select, {
                options: filteredOrderAdmins,
                modelValue: row.order.order_administrator,
                "track-by": "id",
                label: "name",
                "onUpdate:modelValue": async (newValue: OrderAdminUser) => {
                  row.order.order_administrator = newValue;
                  const payload = {
                    user_id: newValue ? newValue.id : null,
                    order_ids: row.order.id,
                  };
                  await store.updateOrderAdmin(payload);

                  await fetchRecords({
                    ...defParams,
                    ...current_filters.value,
                  });
                },
                class: "white-space-nowrap min-w-[200px] max-w-max",
                placeholder: "Assign Admin",
                "append-to-body": true,
              }),
            ],
          );
        },
      },
      {
        key: "",
        label: "Reference",
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id
            ? "bg-gray-100"
            : "";
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) =>
          row.order.customer_reference_no ?? "N/A",
      },
      {
        key: "days_since_action",
        label: "Days Since Action",
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id
            ? "bg-gray-100"
            : "";
        },
        sortable: true,
        sortKey: "status_updated_at",
        renderer: (val: any, row: DashboardItem) =>
          timeAgo(row.order.created_at ?? Date.now().toString()),
      },
    ].filter(Boolean);
  }

  return baseColumns;
});

const columns = computed(() => visibleColumns.value);

const getQuickFilterData = async (filter: any) => {
  if (filter && typeof filter === "object" && "items_for" in filter) {
    const { items_for, quick_filters, urgent_orders } = filter;
    defParams = {
      ...defParams,
      page: 1,
      items_for: items_for ?? currentTab.value ?? "all_items",
      urgent_orders:
        urgent_orders !== undefined
          ? urgent_orders
          : selectedUrgentOrderFilter.value,
    };
    current_filters.value = { ...defParams };
    localStorage.setItem(
      "dashboardRequest",
      JSON.stringify({ dashboardRequest: current_filters.value }),
    );
    dashboardQuickFilters.value = quick_filters ? { ...quick_filters } : {};
    selectedFilter.value = items_for;
    selectedUrgentOrderFilter.value =
      urgent_orders !== undefined
        ? urgent_orders
        : selectedUrgentOrderFilter.value;
  } else {
    // Fallback: treat as classic quick filter
    selectedFilter.value =
      filter && typeof filter === "object" && "items_for" in filter
        ? filter.items_for
        : filter;
    defParams = {
      ...defParams,
      page: 1,
      items_for: selectedFilter.value ?? currentTab.value ?? "all_items",
      urgent_orders:
        filter?.urgent_orders !== undefined
          ? filter?.urgent_orders
          : selectedUrgentOrderFilter.value,
    };
    current_filters.value = { ...defParams };
    localStorage.setItem(
      "dashboardRequest",
      JSON.stringify({ dashboardRequest: current_filters.value }),
    );
    dashboardQuickFilters.value = {};
    selectedUrgentOrderFilter.value =
      filter?.urgent_orders !== undefined
        ? filter?.urgent_orders
        : selectedUrgentOrderFilter.value;
  }
};

// Re-enabled with better logic to only fetch when year/month changes
watch(
  [
    () => dashboardStore.queryParams,
    () => dashboardStore.calendarFilterPayload,
  ],
  async ([newParams, newPayload], [oldParams, oldPayload]) => {
    // Only fetch calendar data if we're in calendar mode
    if (currentMode.value === "calendar") {
      // Check if the params actually changed (not just minor changes)
      if (newParams !== oldParams || newPayload !== oldPayload) {
        await dashboardStore.fetchCalendarData();
      }
    }
  },
  { immediate: true, deep: true },
);

const handleResize = (sizes: { first: number; second: number }) => {
  currentSplitterSize.value = sizes.first;
  splitterSize.value = sizes.first;
  localStorage.setItem("dashboard-splitter-size", sizes.first.toString());
};

const currentOrderItem: ComputedRef<OrderDetailItem | null> = computed(() => {
  return selectedOrderDetails.value
    ? selectedOrderDetails.value.items.find(
        (item) => item.factory_id === selectedRow.value.factory_id,
      )
    : null;
});

const currentOrderItemIndex: ComputedRef<number | null> = computed(() => {
  return selectedOrderDetails.value
    ? selectedOrderDetails.value.items.findIndex(
        (item) => item.factory_id === selectedRow.value.factory_id,
      )
    : null;
});
const handleCellClick = async ({
  row,
  column,
}: {
  row: DashboardItem;
  column: Column;
}) => {
  selectedRow.value = row;
  const orderData = await store.getOrderDetail(row.order.id);
  selectedOrderDetails.value = orderData;
  sidebarOpen.value = false;
};

const handleTabChange = (tabId: string) => {
  console.log(`Tab changed to: ${tabId}`);
};

const handleOrderMoved = async (responseData: any) => {
  // Show loader
  isSidepaneLoading.value = true;

  try {
    // Refetch order details to show updated data
    if (selectedRow.value) {
      const orderData = await store.getOrderDetail(selectedRow.value.order.id);
      selectedOrderDetails.value = orderData;
    }

    // Also refresh the main table data
    await fetchOnSave(true);
  } catch (error) {
    console.error("Error refetching order details:", error);
  } finally {
    // Hide loader
    isSidepaneLoading.value = false;
  }
};

const handleOrderUpdated = async (orderItem: OrderDetailItem) => {
  // Show loader
  isSidepaneLoading.value = true;

  try {
    // Refetch order details to show updated data
    if (selectedRow.value) {
      const orderData = await store.getOrderDetail(selectedRow.value.order.id);
      selectedOrderDetails.value = orderData;
    }

    // Also refresh the main table data
    await fetchOnSave(true);
  } catch (error) {
    console.error("Error refetching order details:", error);
  } finally {
    // Hide loader
    isSidepaneLoading.value = false;
  }
};
</script>
<style lang="scss">
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 1000;
  width: 100vw;
  background-color: #00000095;
  display: flex;
  justify-content: center;
  align-items: center;
}
.tooltip-container {
  .tooltip {
    opacity: 0;
    z-index: -1;
    transition: all 0.3s ease;
    transform: translateX(-30px) translateY(-3px);
    border-right: 4px solid #00b087;
    color: black;
    box-shadow: 0px 0px 10px #999999;
    padding: 5px 4px;
    white-space: nowrap;
    border-radius: 4px;
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: -7px;
      border-width: 5px;
      border-style: solid;
      transform: translateY(-50%);
      border-color: transparent white transparent transparent;
    }
  }
  &:hover {
    .tooltip {
      opacity: 1;
      transform: translateX(40px) translateY(-3px);
      z-index: 1;
    }
  }
}
</style>
